import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Route,
  Calendar,
  Play,
  StopCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  Clock,
  MapPin,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Badge,
  Progress,
  Input,
  Label,
} from '@flote/ui';
import { deliveryTsr } from '../../api';
import type { RouteOptimizationMode } from '@flote/types/delivery';
import { getErrorMessage } from '../../lib/error-utils';
import { getDeliveryAdminApiBaseUrl } from '../../lib/api-base-url';

type SelectionMode = 'team' | 'manual';
type OptimizationState = 'idle' | 'initializing' | 'running' | 'success' | 'failed' | 'applying' | 'applied';

interface OptimizationIssue {
  type: string;
  message: string;
  entityId?: string;
  reason?: string;
}

interface OptimizationSummary {
  assigned: number;
  unassigned: number;
  cost?: number;
  metrics?: {
    totalTransitTime?: number;
    totalTransitDistance?: number;
    totalServiceTime?: number;
  };
  workers?: Record<string, {
    travelDistance: number;
    travelTime: number;
    taskCount: number;
  }>;
}

const deliveryAdminApiBaseUrl = getDeliveryAdminApiBaseUrl();

export function OptimizePage() {
  // Selection state
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('team');
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]!
  );
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);

  // Parameters
  const [mode, setMode] = useState<RouteOptimizationMode>('minimize_distance');
  const [serviceTime, setServiceTime] = useState<number>(5);

  // Optimization state
  const [state, setState] = useState<OptimizationState>('idle');
  const [optimizationId, setOptimizationId] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [issues, setIssues] = useState<OptimizationIssue[]>([]);
  const [summary, setSummary] = useState<OptimizationSummary | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch teams
  const { data: teamsResponse } = deliveryTsr.adminTeams.list.useQuery({
    queryKey: ['teams-list'],
  });
  const teams = teamsResponse?.status === 200 ? teamsResponse.body : [];

  // Fetch team tasks when team and date are selected
  const { data: teamTasksResponse, isLoading: tasksLoading, refetch: refetchTasks } = deliveryTsr.adminRouteOptimization.getTeamTasks.useQuery({
    queryKey: ['team-tasks', selectedTeamId, selectedDate],
    queryData: { query: { teamId: selectedTeamId, date: selectedDate } },
    enabled: Boolean(selectedTeamId && selectedDate),
  });
  const teamTasks = teamTasksResponse?.status === 200 ? teamTasksResponse.body : [];

  // Mutations
  const initializeMutation = deliveryTsr.adminRouteOptimization.initialize.useMutation();
  const startMutation = deliveryTsr.adminRouteOptimization.start.useMutation();
  const abortMutation = deliveryTsr.adminRouteOptimization.abort.useMutation();
  const applyMutation = deliveryTsr.adminRouteOptimization.apply.useMutation();

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const pollStatus = useCallback(async (optId: string) => {
    try {
      const response = await fetch(
        `${deliveryAdminApiBaseUrl}/api/admin/route-optimization/${optId}/status`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('delivery_admin_api_key') || ''}`,
          },
        }
      );
      const data = await response.json();

      if (data.status === 'pending') {
        setProgress(data.progress || 0);
      } else if (data.status === 'success') {
        setState('success');
        setSummary(data.summary);
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      } else if (data.status === 'failed') {
        setState('failed');
        toast.error('Optimization failed');
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }
    } catch (error) {
      console.error('Failed to poll status:', error);
    }
  }, []);

  const handleOptimize = useCallback(async () => {
    setState('initializing');
    setOptimizationId(null);
    setProgress(0);
    setIssues([]);
    setSummary(null);

    try {
      // Determine task selection
      const taskIds = selectionMode === 'manual' ? selectedTaskIds : undefined;
      const teamId = selectionMode === 'team' ? selectedTeamId : undefined;
      const date = selectionMode === 'team' ? selectedDate : undefined;

      // Initialize optimization
      const initResponse = await initializeMutation.mutateAsync({
        body: {
          teamId,
          date,
          taskIds,
          params: { mode, serviceTimeMinutes: serviceTime },
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });

      if (initResponse.status !== 200) {
        const errorBody = initResponse.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to initialize optimization');
        setState('idle');
        return;
      }

      const { optimizationId: optId, issues: initIssues } = initResponse.body;
      setOptimizationId(optId);
      setIssues(initIssues);

      if (initIssues.length > 0) {
        toast.warning(`Initialized with ${initIssues.length} issues`);
      }

      // Start optimization
      const startResponse = await startMutation.mutateAsync({
        params: { optimizationId: optId },
      });

      if (startResponse.status !== 200) {
        const errorBody = startResponse.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to start optimization');
        setState('idle');
        return;
      }

      setState('running');
      toast.success('Optimization started');

      // Start polling
      pollingRef.current = setInterval(() => pollStatus(optId), 2000);
    } catch (error) {
      console.error('Optimization error:', error);
      toast.error(getErrorMessage(error, 'Failed to start optimization'));
      setState('idle');
    }
  }, [
    selectionMode,
    selectedTaskIds,
    selectedTeamId,
    selectedDate,
    mode,
    serviceTime,
    initializeMutation,
    startMutation,
    pollStatus,
  ]);

  const handleAbort = useCallback(async () => {
    if (!optimizationId) return;

    try {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }

      const response = await abortMutation.mutateAsync({
        params: { optimizationId },
      });

      if (response.status === 200) {
        toast.info('Optimization aborted');
        setState('idle');
      } else {
        const errorBody = response.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to abort optimization');
      }
    } catch (error) {
      console.error('Abort error:', error);
      toast.error(getErrorMessage(error, 'Failed to abort optimization'));
    }
  }, [optimizationId, abortMutation]);

  const handleApply = useCallback(async () => {
    if (!optimizationId) return;

    setState('applying');
    try {
      const response = await applyMutation.mutateAsync({
        params: { optimizationId },
      });

      if (response.status === 200) {
        toast.success('Optimization results applied!');
        setState('applied');
        refetchTasks();
      } else {
        const errorBody = response.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to apply optimization');
        setState('success');
      }
    } catch (error) {
      console.error('Apply error:', error);
      toast.error(getErrorMessage(error, 'Failed to apply optimization'));
      setState('success');
    }
  }, [optimizationId, applyMutation, refetchTasks]);

  const handleReset = useCallback(() => {
    setState('idle');
    setOptimizationId(null);
    setProgress(0);
    setIssues([]);
    setSummary(null);
    setSelectedTaskIds([]);
  }, []);

  const handleTaskToggle = useCallback((taskId: string) => {
    setSelectedTaskIds((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedTaskIds.length === teamTasks.length) {
      setSelectedTaskIds([]);
    } else {
      setSelectedTaskIds(teamTasks.map((t) => t.onfleetTaskId));
    }
  }, [selectedTaskIds.length, teamTasks]);

  const canOptimize =
    state === 'idle' &&
    ((selectionMode === 'team' && selectedTeamId && selectedDate) ||
      (selectionMode === 'manual' && selectedTaskIds.length > 0));

  const isProcessing = state === 'initializing' || state === 'running' || state === 'applying';

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const formatDistance = (meters: number) => {
    const miles = meters / 1609.34;
    return `${miles.toFixed(1)} mi`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Route Optimization</h1>
        <p className="text-muted-foreground">
          Optimize driver routes to minimize distance or ensure on-time deliveries
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Panel: Task Selection */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Select Tasks</CardTitle>
              <CardDescription>
                Choose tasks to include in route optimization
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs
                value={selectionMode}
                onValueChange={(v) => setSelectionMode(v as SelectionMode)}
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="team" disabled={isProcessing}>
                    <Calendar className="mr-2 h-4 w-4" />
                    By Team & Date
                  </TabsTrigger>
                  <TabsTrigger value="manual" disabled={isProcessing}>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Manual Selection
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="team" className="mt-4 space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Team</Label>
                      <Select
                        value={selectedTeamId}
                        onValueChange={setSelectedTeamId}
                        disabled={isProcessing}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a team" />
                        </SelectTrigger>
                        <SelectContent>
                          {teams.map((team) => (
                            <SelectItem key={team.id} value={team.id}>
                              {team.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        disabled={isProcessing}
                      />
                    </div>
                  </div>

                  {selectedTeamId && selectedDate && (
                    <div className="rounded-lg border p-4">
                      {tasksLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : teamTasks.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                          <AlertTriangle className="mb-2 h-8 w-8 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">
                            No unassigned tasks found for this team and date
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">
                              {teamTasks.length} unassigned tasks found
                            </span>
                            <Badge variant="secondary">
                              <Clock className="mr-1 h-3 w-3" />
                              Ready to optimize
                            </Badge>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="manual" className="mt-4">
                  {/* First select team to load tasks for manual selection */}
                  <div className="mb-4 space-y-2">
                    <Label>Team (to load tasks)</Label>
                    <Select
                      value={selectedTeamId}
                      onValueChange={setSelectedTeamId}
                      disabled={isProcessing}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a team" />
                      </SelectTrigger>
                      <SelectContent>
                        {teams.map((team) => (
                          <SelectItem key={team.id} value={team.id}>
                            {team.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedTeamId && (
                    <>
                      {tasksLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : teamTasks.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                          <AlertTriangle className="mb-2 h-8 w-8 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">
                            No unassigned tasks found
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleSelectAll}
                              disabled={isProcessing}
                            >
                              {selectedTaskIds.length === teamTasks.length
                                ? 'Deselect All'
                                : 'Select All'}
                            </Button>
                            <span className="text-sm text-muted-foreground">
                              {selectedTaskIds.length} of {teamTasks.length} selected
                            </span>
                          </div>

                          <div className="max-h-[400px] overflow-y-auto rounded-lg border">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="w-12"></TableHead>
                                  <TableHead>Recipient</TableHead>
                                  <TableHead className="hidden sm:table-cell">Address</TableHead>
                                  <TableHead className="hidden md:table-cell">Notes</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {teamTasks.map((task) => (
                                  <TableRow
                                    key={task.onfleetTaskId}
                                    className="cursor-pointer"
                                    onClick={() => handleTaskToggle(task.onfleetTaskId)}
                                  >
                                    <TableCell>
                                      <Checkbox
                                        checked={selectedTaskIds.includes(task.onfleetTaskId)}
                                        onCheckedChange={() => handleTaskToggle(task.onfleetTaskId)}
                                        disabled={isProcessing}
                                      />
                                    </TableCell>
                                    <TableCell className="font-medium">
                                      {task.recipientName || 'Unknown'}
                                    </TableCell>
                                    <TableCell className="hidden sm:table-cell text-muted-foreground">
                                      <div className="flex items-center gap-1">
                                        <MapPin className="h-3 w-3" />
                                        {task.address ? `${task.address.number} ${task.address.street}` : 'No address'}
                                      </div>
                                    </TableCell>
                                    <TableCell className="hidden md:table-cell text-muted-foreground max-w-[200px] truncate">
                                      {task.notes || '-'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Parameters */}
          <Card>
            <CardHeader>
              <CardTitle>Optimization Parameters</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Optimization Mode</Label>
                  <Select
                    value={mode}
                    onValueChange={(v) => setMode(v as RouteOptimizationMode)}
                    disabled={isProcessing}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minimize_distance">
                        Minimize Distance
                      </SelectItem>
                      <SelectItem value="always_on_time">
                        Always On Time
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {mode === 'minimize_distance'
                      ? 'Reduce total travel distance'
                      : 'Prioritize meeting time windows'}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Service Time (minutes per stop)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={60}
                    value={serviceTime}
                    onChange={(e) => setServiceTime(Number(e.target.value))}
                    disabled={isProcessing}
                  />
                  <p className="text-xs text-muted-foreground">
                    Time spent at each delivery location
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel: Action & Results */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Optimization</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {state === 'idle' && (
                <Button
                  className="w-full"
                  size="lg"
                  onClick={handleOptimize}
                  disabled={!canOptimize}
                >
                  <Route className="mr-2 h-5 w-5" />
                  Optimize Routes
                </Button>
              )}

              {state === 'initializing' && (
                <div className="flex flex-col items-center py-4">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="mt-2 text-sm text-muted-foreground">
                    Initializing optimization...
                  </p>
                </div>
              )}

              {state === 'running' && (
                <div className="space-y-4">
                  <div className="flex flex-col items-center py-4">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="mt-2 text-sm text-muted-foreground">
                      Calculating optimal routes...
                    </p>
                  </div>
                  <Progress value={progress} className="w-full" />
                  <p className="text-center text-sm text-muted-foreground">
                    {progress}% complete
                  </p>
                  <Button
                    variant="destructive"
                    className="w-full"
                    onClick={handleAbort}
                  >
                    <StopCircle className="mr-2 h-4 w-4" />
                    Abort
                  </Button>
                </div>
              )}

              {state === 'success' && summary && (
                <div className="space-y-4">
                  <div className="flex items-center justify-center py-2">
                    <CheckCircle2 className="h-8 w-8 text-green-500" />
                  </div>
                  <p className="text-center font-medium">Optimization Complete!</p>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-lg bg-green-50 p-3 text-center">
                      <p className="text-2xl font-bold text-green-700">
                        {summary.assigned}
                      </p>
                      <p className="text-green-600">Assigned</p>
                    </div>
                    <div className="rounded-lg bg-amber-50 p-3 text-center">
                      <p className="text-2xl font-bold text-amber-700">
                        {summary.unassigned}
                      </p>
                      <p className="text-amber-600">Unassigned</p>
                    </div>
                  </div>

                  {summary.metrics && (
                    <div className="space-y-2 text-sm">
                      {summary.metrics.totalTransitDistance && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Total Distance:</span>
                          <span className="font-medium">
                            {formatDistance(summary.metrics.totalTransitDistance)}
                          </span>
                        </div>
                      )}
                      {summary.metrics.totalTransitTime && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Total Drive Time:</span>
                          <span className="font-medium">
                            {formatDuration(summary.metrics.totalTransitTime)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={handleReset}
                    >
                      Discard
                    </Button>
                    <Button className="flex-1" onClick={handleApply}>
                      <Play className="mr-2 h-4 w-4" />
                      Apply
                    </Button>
                  </div>
                </div>
              )}

              {state === 'applying' && (
                <div className="flex flex-col items-center py-4">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="mt-2 text-sm text-muted-foreground">
                    Applying optimization...
                  </p>
                </div>
              )}

              {state === 'applied' && (
                <div className="space-y-4">
                  <div className="flex flex-col items-center py-4">
                    <CheckCircle2 className="h-12 w-12 text-green-500" />
                    <p className="mt-2 font-medium text-green-700">
                      Routes Applied Successfully!
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Tasks have been assigned to drivers
                    </p>
                  </div>
                  <Button className="w-full" onClick={handleReset}>
                    Start New Optimization
                  </Button>
                </div>
              )}

              {state === 'failed' && (
                <div className="space-y-4">
                  <div className="flex flex-col items-center py-4">
                    <XCircle className="h-12 w-12 text-red-500" />
                    <p className="mt-2 font-medium text-red-700">
                      Optimization Failed
                    </p>
                  </div>
                  <Button className="w-full" onClick={handleReset}>
                    Try Again
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Issues */}
          {issues.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  <AlertTriangle className="mr-2 inline h-4 w-4 text-amber-500" />
                  Issues ({issues.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-[200px] space-y-2 overflow-y-auto">
                  {issues.map((issue, idx) => (
                    <div
                      key={idx}
                      className="rounded-lg bg-amber-50 p-2 text-sm"
                    >
                      <p className="font-medium text-amber-800">{issue.type}</p>
                      <p className="text-amber-600">{issue.message}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Worker breakdown */}
          {state === 'success' && summary?.workers && Object.keys(summary.workers).length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  <Users className="mr-2 inline h-4 w-4" />
                  Driver Assignments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(summary.workers).map(([workerId, workerData]) => (
                    <div
                      key={workerId}
                      className="flex items-center justify-between rounded-lg bg-muted/50 p-2 text-sm"
                    >
                      <span className="font-medium truncate max-w-[120px]">
                        {workerId.slice(-8)}
                      </span>
                      <div className="flex gap-3 text-muted-foreground">
                        <span>{workerData.taskCount} tasks</span>
                        <span>{formatDistance(workerData.travelDistance)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
